import { useState } from "react";
import OtpInput from "react-otp-input";
import Spinner from "sushiweb/Spinner/Spinner";
import Drawer from "components/shared/Drawer/Drawer";
import ShowToast from "../Common/Toast";
import { Button } from "../Common/styles.common";
import { OTP_LENGTH } from "utils/constants/others";
import { OtpContainer, OtpInputField } from "./styles.grn";
import { EmailErrorText, EmailHintText, EmailInput, EmailInputContainer } from "./styles.email";
import { StepBody, StepFooter, StepHeading, StepLayout, StepSubheading } from "./styles.grnDesktop";
import GrnDisputeItems from "./GrnDisputeItems";
import useGrnDisputeLogic from "./useGrnDisputeLogic";
import { useGrnEmailEntry, useGrnOtpVerification } from "./useGrnEmailVerification";
import { strings } from "utils/constants/strings";

const STEP = { DISPUTE: "dispute", EMAIL: "email", OTP: "otp" };

const DisputeStep = ({ dispute }) => {
  const { submitDisputeLoading, handleSubmitDispute, showToast, setShowToast, errorMsg, ...itemsProps } = dispute;
  return (
    <StepLayout>
      <StepBody $flush>
        <div style={{ padding: "16px 16px 0 16px" }}>
          <StepHeading style={{ marginBottom: 0 }}>{strings.grnDisputeProductDetails}</StepHeading>
        </div>
        <GrnDisputeItems {...itemsProps} />
      </StepBody>
      <StepFooter>
        <Button
          className="fs-13 text-white fw-600"
          style={{ marginBottom: 0 }}
          onClick={handleSubmitDispute}
          disabled={submitDisputeLoading}
        >
          {submitDisputeLoading ? <Spinner color="white" /> : strings.grnDisputeSubmitDispute}
        </Button>
      </StepFooter>
      {showToast && errorMsg ? <ShowToast errorMsg={errorMsg} type="error" onClose={() => setShowToast(false)} /> : null}
    </StepLayout>
  );
};

const EmailStep = ({ onSent }) => {
  const { email, changeEmail, error, loading, handleVerifyClick } = useGrnEmailEntry({ onSent });
  return (
    <StepLayout>
      <StepBody>
        <StepHeading>{strings.grnDisputeEnterEmail}</StepHeading>
        <EmailInputContainer $hasError={!!error}>
          <EmailInput
            type="email"
            placeholder={strings.grnDisputeEmailPlaceholder}
            value={email}
            $hasError={!!error}
            autoFocus
            onChange={(event) => changeEmail(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && handleVerifyClick()}
          />
          <EmailHintText>
            {strings.grnDisputeEmailHint}
            <br />
            {strings.grnDisputeEmailHintUpdate}
          </EmailHintText>
        </EmailInputContainer>
        {error ? <EmailErrorText>{error}</EmailErrorText> : null}
      </StepBody>
      <StepFooter>
        <Button className="fs-14 text-white fw-600" style={{ marginBottom: 0 }} onClick={handleVerifyClick} disabled={loading}>
          {loading ? <Spinner color="white" /> : strings.grnDisputeVerifyOtp}
        </Button>
      </StepFooter>
    </StepLayout>
  );
};

const formatTimer = (seconds) => `00:${String(seconds).padStart(2, "0")}`;

const OtpStep = ({ email, onVerified }) => {
  const otpState = useGrnOtpVerification({ email, onVerified });
  const { otp, setOtp, loading, error, timer, showResend, handleResend, handleVerifyAndUpdate, isOtpComplete } = otpState;

  return (
    <StepLayout>
      <StepBody>
        <StepHeading $large>{strings.grnDisputeVerifyEmail}</StepHeading>
        <StepSubheading>
          Enter the {OTP_LENGTH}-digit OTP sent to <strong>{email}</strong>
        </StepSubheading>
        <OtpContainer>
          <OtpInput
            value={otp}
            onChange={setOtp}
            numInputs={OTP_LENGTH}
            containerStyle={{ display: "flex", gap: "12px", justifyContent: "center" }}
            renderInput={(props) => (
              <OtpInputField
                {...props}
                style={{ borderBottomColor: error ? "#EF4F5F" : otp.length > 0 ? "#293142" : "#E6E9EF" }}
              />
            )}
            shouldAutoFocus
          />
        </OtpContainer>
        {error ? (
          <div style={{ textAlign: "center", marginTop: "8px" }}>
            <span style={{ fontSize: "13px", color: "#EF4F5F" }}>{error}</span>
          </div>
        ) : null}
        <div style={{ textAlign: "center", marginTop: "16px" }}>
          {showResend ? (
            <button
              type="button"
              onClick={handleResend}
              disabled={loading}
              style={{
                background: "none",
                border: "none",
                fontSize: "14px",
                fontWeight: 600,
                color: "#EF4F5F",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
                padding: 0,
              }}
            >
              {strings.grnDisputeResendOtp}
            </button>
          ) : (
            <span style={{ fontSize: "13px", color: "#767C8F" }}>
              <span style={{ fontWeight: 600, color: "#293142" }}>{formatTimer(timer)}</span> {strings.grnDisputeResendOtp}
            </span>
          )}
        </div>
      </StepBody>
      <StepFooter>
        <Button
          className="fs-14 text-white fw-600"
          style={{ marginBottom: 0 }}
          onClick={handleVerifyAndUpdate}
          disabled={loading || !isOtpComplete}
        >
          {loading ? <Spinner color="white" /> : strings.grnDisputeVerifyAndAddEmail}
        </Button>
      </StepFooter>
      {otpState.showToast && otpState.verifyAndUpdateEmailErr ? (
        <ShowToast
          errorMsg={otpState.verifyAndUpdateEmailErr}
          type="error"
          onClose={() => otpState.setShowToast(false)}
        />
      ) : null}
    </StepLayout>
  );
};

/**
 * Desktop "Raise dispute" sidebar. Steps: dispute form -> (no email on profile) enter email -> verify OTP -> back to the form.
 * The form state lives in useGrnDisputeLogic at this level so it survives the email steps.
 */
const GrnDisputeDrawer = ({ grn, grnItems, source, purchaseOrderId, grnId, onClose }) => {
  const [step, setStep] = useState(STEP.DISPUTE);
  const [email, setEmail] = useState("");

  const dispute = useGrnDisputeLogic({
    grn,
    grnItems,
    source,
    purchaseOrderId,
    grnId,
    onNeedEmail: () => setStep(STEP.EMAIL),
  });

  const onBack = step === STEP.OTP ? () => setStep(STEP.EMAIL) : step === STEP.EMAIL ? () => setStep(STEP.DISPUTE) : undefined;

  return (
    <Drawer
      title={strings.grnDisputeTitle}
      onClose={onClose}
      onBack={onBack}
      plainBack
      scrollable={false}
      overlayDisabled={dispute.submitDisputeLoading}
    >
      {step === STEP.DISPUTE ? <DisputeStep dispute={dispute} /> : null}
      {step === STEP.EMAIL ? (
        <EmailStep
          onSent={(verifiedEmail) => {
            setEmail(verifiedEmail);
            setStep(STEP.OTP);
          }}
        />
      ) : null}
      {step === STEP.OTP ? <OtpStep email={email} onVerified={() => setStep(STEP.DISPUTE)} /> : null}
    </Drawer>
  );
};

export default GrnDisputeDrawer;
